import React from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import {
  ArrowLeft,
  ArrowRight,
  BarChart3,
  Building2,
  CheckCircle2,
  ChevronRight,
  Clock,
  CreditCard,
  FileText,
  Filter,
  Landmark,
  LayoutDashboard,
  Lightbulb,
  LinkIcon,
  Receipt,
  Search,
  ShieldCheck,
  Sparkles,
  TrendingUp,
  Upload,
  WalletCards
} from "lucide-react";

export default function BillingTutorialPage() {
  const { user } = useAuth();

  return (
    <div className="min-h-screen bg-slate-50 text-gray-900 font-sans pb-20">
      <header className="border-b bg-white sticky top-0 z-40 shadow-sm">
        <div className="max-w-4xl mx-auto px-4 py-3 flex flex-col md:flex-row justify-between items-center gap-4">
          <Link to="/">
            <img
              src="/marketing/logo_official.png"
              className="h-28 object-contain"
              alt="OdontoAssist AI Logo"
            />
          </Link>

          <div className="flex items-center gap-6">
            <Link
              to="/ajuda/tutoriais"
              className="text-gray-500 hover:text-indigo-600 font-semibold hidden md:block"
            >
              Central de Guias
            </Link>

            <Link
              to="/"
              className="bg-indigo-600 hover:bg-indigo-700 text-white px-6 py-3 rounded-2xl font-black tracking-wide flex items-center gap-2 transition-all shadow-lg hover:-translate-y-1"
            >
              <ArrowLeft size={20} />
              Voltar {user ? "para o Aplicativo" : "ao Início"}
            </Link>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-10">
        <div className="flex items-center gap-2 text-sm font-semibold text-gray-500 mb-6">
          <Link to="/ajuda" className="hover:text-indigo-600">
            Ajuda
          </Link>
          <ChevronRight size={14} className="text-gray-400" />
          <Link to="/ajuda/tutoriais" className="hover:text-indigo-600">
            Guias
          </Link>
          <ChevronRight size={14} className="text-gray-400" />
          <span className="text-indigo-600">Financeiro da Clínica</span>
        </div>

        <h1 className="text-3xl md:text-4xl font-black text-gray-900 mb-4 leading-tight">
          Financeiro da Clínica
        </h1>

        <p className="text-xl text-gray-600 font-semibold mb-6">
          Faturas, recebimentos, despesas, NFS-e, parceiros e relatórios em um único ambiente
        </p>

        <div className="flex flex-wrap items-center gap-6 text-sm text-gray-500 mb-10">
          <div className="flex items-center gap-2">
            <Clock size={16} />
            <span>15 minutos de leitura</span>
          </div>

          <div className="flex items-center gap-2 bg-indigo-50 text-indigo-700 px-3 py-1 rounded-full font-bold uppercase tracking-wider text-xs">
            Gestão e Agenda
          </div>
        </div>

        <article className="prose prose-indigo prose-lg max-w-none text-gray-600 text-justify">
          <p className="lead text-xl text-gray-700 font-medium">
            O módulo <strong>Financeiro</strong> centraliza o controle das receitas
            e despesas da clínica. Ele permite acompanhar desde a criação de uma
            fatura até o recebimento, organizar contas a pagar, importar NFS-e,
            registrar receitas de parceiros e analisar resultados.
          </p>

          <p>
            O antigo espaço de Cobrança e Pagamentos foi ampliado e reorganizado.
            Agora, cada atividade possui uma aba própria e os indicadores são
            calculados conforme o período selecionado.
          </p>

          <div className="bg-indigo-50 border border-indigo-200 rounded-3xl p-6 my-8">
            <h3 className="text-lg font-bold text-indigo-900 mt-0 mb-2 flex items-center gap-2">
              <WalletCards size={22} />
              Um fluxo financeiro completo
            </h3>
            <p className="text-indigo-800 mb-0">
              O ideal é registrar a cobrança em <strong>Faturas</strong>, lançar
              o valor pago em <strong>Recebimentos</strong>, manter as
              <strong> Despesas</strong> atualizadas e usar os
              <strong> Relatórios</strong> para conferir o resultado.
            </p>
          </div>

          <h2 className="text-2xl font-bold text-gray-900 mt-10 mb-4">
            Conhecendo o Financeiro
          </h2>

          <div className="space-y-8 mt-8">
            <div className="bg-white p-8 rounded-3xl border border-gray-100 shadow-sm relative overflow-hidden">
              <div className="absolute top-0 left-0 w-2 h-full bg-indigo-500" />
              <h3 className="text-xl font-bold text-gray-900 mb-3 flex items-center gap-3">
                <span className="w-8 h-8 rounded-full bg-indigo-100 text-indigo-700 flex items-center justify-center text-sm font-black">1</span>
                Período e visão geral
              </h3>
              <p>
                O período escolhido no topo da página é aplicado às faturas,
                recebimentos, despesas e relatórios. Você pode visualizar:
              </p>
              <ul>
                <li>mês atual;</li>
                <li>um mês específico;</li>
                <li>um intervalo personalizado;</li>
                <li>todo o histórico, quando essa opção estiver disponível.</li>
              </ul>
              <div className="bg-amber-50 border border-amber-200 rounded-2xl p-5 mt-5">
                <p className="text-amber-900 mb-0">
                  Antes de comparar valores, confirme sempre o período selecionado.
                  Uma fatura, um recebimento ou uma despesa pode não aparecer porque
                  está fora do intervalo atual.
                </p>
              </div>
            </div>

            <div className="bg-white p-8 rounded-3xl border border-gray-100 shadow-sm relative overflow-hidden">
              <div className="absolute top-0 left-0 w-2 h-full bg-indigo-500" />
              <h3 className="text-xl font-bold text-gray-900 mb-3 flex items-center gap-3">
                <span className="w-8 h-8 rounded-full bg-indigo-100 text-indigo-700 flex items-center justify-center text-sm font-black">2</span>
                Faturas e receitas
              </h3>
              <p>
                A aba <strong>Faturas</strong> é o centro das cobranças da clínica.
                Ela reúne cobranças de pacientes e receitas externas.
              </p>
              <div className="grid md:grid-cols-2 gap-4 mt-5">
                <div className="bg-slate-50 p-4 rounded-xl border border-slate-200">
                  <h4 className="font-bold text-gray-900 flex items-center gap-2 mb-2">
                    <FileText size={20} className="text-indigo-600" />
                    Fatura de paciente
                  </h4>
                  <p className="text-sm mb-0">
                    Selecione o paciente, informe os itens, valores, descontos,
                    vencimento e condições de pagamento. A cobrança pode ser única
                    ou parcelada.
                  </p>
                </div>
                <div className="bg-slate-50 p-4 rounded-xl border border-slate-200">
                  <h4 className="font-bold text-gray-900 flex items-center gap-2 mb-2">
                    <Building2 size={20} className="text-indigo-600" />
                    Receita externa
                  </h4>
                  <p className="text-sm mb-0">
                    Registre valores que não pertencem diretamente a um paciente,
                    como receitas de parceiros, cursos, palestras, comissões ou
                    consultorias.
                  </p>
                </div>
              </div>

              <p className="mt-5">Os principais status são:</p>
              <ul>
                <li><strong>Pendente:</strong> ainda não recebeu pagamento;</li>
                <li><strong>Parcialmente pago:</strong> recebeu apenas parte do valor;</li>
                <li><strong>Pago:</strong> cobrança de paciente quitada;</li>
                <li><strong>Vencido:</strong> saldo em aberto após o vencimento;</li>
                <li><strong>Aguardando recebimento:</strong> receita externa faturada;</li>
                <li><strong>Recebido parcialmente ou recebido:</strong> situação da receita externa.</li>
              </ul>

              <div className="grid md:grid-cols-3 gap-4 mt-5">
                {[
                  ["Em aberto", "Valores ainda aguardando pagamento."],
                  ["Vencido", "Cobranças que exigem acompanhamento."],
                  ["Receita externa", "Valores registrados para parceiros ou outras origens."]
                ].map(([title, description]) => (
                  <div key={title} className="bg-slate-50 p-4 rounded-xl border border-slate-200">
                    <h4 className="font-bold text-indigo-700 mb-2">{title}</h4>
                    <p className="text-sm mb-0">{description}</p>
                  </div>
                ))}
              </div>
            </div>

            <div className="bg-white p-8 rounded-3xl border border-gray-100 shadow-sm relative overflow-hidden">
              <div className="absolute top-0 left-0 w-2 h-full bg-indigo-500" />
              <h3 className="text-xl font-bold text-gray-900 mb-3 flex items-center gap-3">
                <span className="w-8 h-8 rounded-full bg-indigo-100 text-indigo-700 flex items-center justify-center text-sm font-black">3</span>
                Parcelamento e créditos do paciente
              </h3>
              <p>
                Uma fatura pode ser criada com pagamento único ou parcelado. Em
                faturas parceladas, cada parcela possui seu próprio vencimento e
                pode receber um pagamento específico.
              </p>
              <p>
                O sistema também permite registrar crédito antecipado do paciente.
                Esse saldo pode ser utilizado posteriormente para quitar total ou
                parcialmente uma cobrança.
              </p>
              <div className="bg-blue-50 border border-blue-200 rounded-2xl p-5 mt-5">
                <p className="text-blue-900 mb-0">
                  Ao registrar um pagamento, confira se ele está sendo vinculado à
                  fatura inteira, a uma parcela específica ou ao crédito do paciente.
                </p>
              </div>
            </div>

            <div className="bg-white p-8 rounded-3xl border border-gray-100 shadow-sm relative overflow-hidden">
              <div className="absolute top-0 left-0 w-2 h-full bg-indigo-500" />
              <h3 className="text-xl font-bold text-gray-900 mb-3 flex items-center gap-3">
                <span className="w-8 h-8 rounded-full bg-indigo-100 text-indigo-700 flex items-center justify-center text-sm font-black">4</span>
                Recebimentos
              </h3>
              <p>
                A aba <strong>Recebimentos</strong> reúne os valores efetivamente
                registrados como pagos.
              </p>
              <p>Ela apresenta, entre outros dados:</p>
              <ul>
                <li>total recebido no período;</li>
                <li>quantidade de recebimentos confirmados;</li>
                <li>valor médio;</li>
                <li>forma de pagamento mais utilizada;</li>
                <li>origem do recebimento;</li>
                <li>fatura ou parcela vinculada.</li>
              </ul>
              <div className="grid md:grid-cols-2 gap-4 mt-5">
                <div className="bg-slate-50 p-4 rounded-xl border border-slate-200">
                  <h4 className="font-bold text-gray-900 flex items-center gap-2 mb-2">
                    <CreditCard size={20} className="text-indigo-600" />
                    Formas de pagamento
                  </h4>
                  <p className="text-sm mb-0">
                    Utilize a forma correta, como Pix, dinheiro, cartão ou
                    transferência. Isso melhora a qualidade dos relatórios.
                  </p>
                </div>
                <div className="bg-slate-50 p-4 rounded-xl border border-slate-200">
                  <h4 className="font-bold text-gray-900 flex items-center gap-2 mb-2">
                    <Search size={20} className="text-indigo-600" />
                    Pesquisa e filtros
                  </h4>
                  <p className="text-sm mb-0">
                    Localize por paciente, referência, transação, forma, status
                    ou origem do recebimento.
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-white p-8 rounded-3xl border border-gray-100 shadow-sm relative overflow-hidden">
              <div className="absolute top-0 left-0 w-2 h-full bg-indigo-500" />
              <h3 className="text-xl font-bold text-gray-900 mb-3 flex items-center gap-3">
                <span className="w-8 h-8 rounded-full bg-indigo-100 text-indigo-700 flex items-center justify-center text-sm font-black">5</span>
                Despesas
              </h3>
              <p>
                A aba <strong>Despesas</strong> organiza as contas da clínica.
                Utilize-a para registrar custos, compromissos e pagamentos.
              </p>
              <ul>
                <li>descrição e categoria;</li>
                <li>fornecedor;</li>
                <li>competência e vencimento;</li>
                <li>valor;</li>
                <li>parcelas;</li>
                <li>situação do pagamento;</li>
                <li>forma e data de pagamento;</li>
                <li>observações e documentos relacionados, quando disponíveis.</li>
              </ul>
              <div className="bg-amber-50 border border-amber-200 rounded-2xl p-5 mt-5">
                <p className="text-amber-900 mb-0">
                  Registrar apenas a despesa não significa que ela foi paga.
                  Atualize o pagamento quando o valor realmente sair do caixa.
                </p>
              </div>
            </div>

            <div className="bg-white p-8 rounded-3xl border border-gray-100 shadow-sm relative overflow-hidden">
              <div className="absolute top-0 left-0 w-2 h-full bg-indigo-500" />
              <h3 className="text-xl font-bold text-gray-900 mb-3 flex items-center gap-3">
                <span className="w-8 h-8 rounded-full bg-indigo-100 text-indigo-700 flex items-center justify-center text-sm font-black">6</span>
                NFS-e Importadas
              </h3>
              <p>
                A aba <strong>NFS-e Importadas</strong> centraliza notas fiscais
                eletrônicas recebidas em formatos aceitos pelo sistema.
              </p>
              <div className="grid md:grid-cols-2 gap-4 mt-5">
                <div className="bg-slate-50 p-4 rounded-xl border border-slate-200">
                  <h4 className="font-bold text-gray-900 flex items-center gap-2 mb-2">
                    <Upload size={20} className="text-indigo-600" />
                    Importação
                  </h4>
                  <p className="text-sm mb-0">
                    Selecione o arquivo da nota. O sistema tenta identificar
                    número, emissão, prestador, tomador, CPF/CNPJ e valor do serviço.
                  </p>
                </div>
                <div className="bg-slate-50 p-4 rounded-xl border border-slate-200">
                  <h4 className="font-bold text-gray-900 flex items-center gap-2 mb-2">
                    <LinkIcon size={20} className="text-indigo-600" />
                    Vínculo
                  </h4>
                  <p className="text-sm mb-0">
                    Vincule a nota à fatura correspondente para manter o histórico
                    financeiro e fiscal organizado.
                  </p>
                </div>
              </div>

              <p className="mt-5">
                A tela mostra totais, notas vinculadas, notas aguardando vínculo,
                valor total e valor ainda não relacionado a faturas. Também permite
                pesquisar, ordenar, filtrar por tipo de arquivo e visualizar o
                documento importado.
              </p>

              <div className="bg-red-50 border border-red-200 rounded-2xl p-5 mt-5">
                <p className="text-red-900 mb-0">
                  Arquivos sem dados mínimos reconhecíveis podem ser rejeitados.
                  Confira se o documento é realmente uma NFS-e válida e legível antes
                  de tentar novamente.
                </p>
              </div>
            </div>

            <div className="bg-white p-8 rounded-3xl border border-gray-100 shadow-sm relative overflow-hidden">
              <div className="absolute top-0 left-0 w-2 h-full bg-indigo-500" />
              <h3 className="text-xl font-bold text-gray-900 mb-3 flex items-center gap-3">
                <span className="w-8 h-8 rounded-full bg-indigo-100 text-indigo-700 flex items-center justify-center text-sm font-black">7</span>
                Parceiros e receitas externas
              </h3>
              <p>
                A aba <strong>Parceiros</strong> organiza empresas e instituições
                que geram receitas externas para a clínica ou para o profissional.
              </p>
              <p>Exemplos:</p>
              <ul>
                <li>SouSmile e outras redes de alinhadores;</li>
                <li>consultorias;</li>
                <li>cursos, treinamentos e palestras;</li>
                <li>mentorias;</li>
                <li>comissões e bonificações;</li>
                <li>outros contratos ou serviços.</li>
              </ul>
              <p>
                Mantenha o cadastro do parceiro atualizado e utilize-o ao criar uma
                receita externa. Isso permite comparar resultados por parceiro e
                categoria de serviço.
              </p>
            </div>

            <div className="bg-white p-8 rounded-3xl border border-gray-100 shadow-sm relative overflow-hidden">
              <div className="absolute top-0 left-0 w-2 h-full bg-indigo-500" />
              <h3 className="text-xl font-bold text-gray-900 mb-3 flex items-center gap-3">
                <span className="w-8 h-8 rounded-full bg-indigo-100 text-indigo-700 flex items-center justify-center text-sm font-black">8</span>
                Relatórios e indicadores
              </h3>
              <p>
                A aba <strong>Relatórios</strong> transforma os lançamentos do
                período em informações gerenciais.
              </p>
              <div className="grid md:grid-cols-2 gap-4 mt-5">
                {[
                  ["Faturamento", "Total das faturas e receitas externas emitidas."],
                  ["Recebimentos", "Valores efetivamente confirmados como recebidos."],
                  ["Em aberto", "Saldo que ainda aguarda pagamento."],
                  ["Vencido", "Saldo em atraso no período."],
                  ["Despesas", "Contas registradas e valores pagos."],
                  ["Resultado operacional", "Recebimentos menos despesas pagas."],
                  ["Margem operacional", "Percentual do resultado em relação aos recebimentos."],
                  ["Fluxo de caixa", "Previsão de entradas e saídas futuras."]
                ].map(([title, description]) => (
                  <div key={title} className="bg-slate-50 p-4 rounded-xl border border-slate-200">
                    <h4 className="font-bold text-indigo-700 mb-2">{title}</h4>
                    <p className="text-sm mb-0">{description}</p>
                  </div>
                ))}
              </div>
              <p className="mt-5">
                Alguns cards e tabelas permitem abrir diretamente as faturas,
                recebimentos ou despesas que formaram o indicador.
              </p>
            </div>

            <div className="bg-white p-8 rounded-3xl border border-gray-100 shadow-sm relative overflow-hidden">
              <div className="absolute top-0 left-0 w-2 h-full bg-indigo-500" />
              <h3 className="text-xl font-bold text-gray-900 mb-3 flex items-center gap-3">
                <span className="w-8 h-8 rounded-full bg-indigo-100 text-indigo-700 flex items-center justify-center text-sm font-black">9</span>
                Pesquisa, filtros e navegação
              </h3>
              <p>
                Cada aba possui filtros próprios. Use-os para reduzir a lista e
                localizar exatamente o lançamento desejado.
              </p>
              <div className="grid md:grid-cols-2 gap-4 mt-5">
                <div className="bg-slate-50 p-4 rounded-xl border border-slate-200">
                  <h4 className="font-bold text-gray-900 flex items-center gap-2 mb-2">
                    <Search size={20} className="text-indigo-600" />
                    Pesquisa
                  </h4>
                  <p className="text-sm mb-0">
                    Procure por paciente, número, parceiro, referência, documento
                    ou outra informação exibida na aba.
                  </p>
                </div>
                <div className="bg-slate-50 p-4 rounded-xl border border-slate-200">
                  <h4 className="font-bold text-gray-900 flex items-center gap-2 mb-2">
                    <Filter size={20} className="text-indigo-600" />
                    Filtros
                  </h4>
                  <p className="text-sm mb-0">
                    Combine status, paciente, forma de pagamento, origem, parceiro,
                    serviço ou vínculo da NFS-e, conforme a área.
                  </p>
                </div>
              </div>
              <p className="mt-5 mb-0">
                Quando você chega ao Financeiro por um atalho do Dashboard, alguns
                filtros podem ser aplicados automaticamente. Use a opção de limpar
                filtros para voltar à lista completa.
              </p>
            </div>

            <div className="bg-white p-8 rounded-3xl border border-gray-100 shadow-sm relative overflow-hidden">
              <div className="absolute top-0 left-0 w-2 h-full bg-indigo-500" />
              <h3 className="text-xl font-bold text-gray-900 mb-3 flex items-center gap-3">
                <span className="w-8 h-8 rounded-full bg-indigo-100 text-indigo-700 flex items-center justify-center text-sm font-black">10</span>
                Fluxo recomendado de uso
              </h3>
              <div className="space-y-3 mt-5">
                {[
                  "Crie a fatura assim que o valor for definido ou o tratamento aprovado.",
                  "Registre o recebimento apenas quando o pagamento for efetivamente confirmado.",
                  "Mantenha despesas, vencimentos e pagamentos atualizados.",
                  "Importe e vincule as NFS-e relacionadas às faturas.",
                  "Cadastre parceiros e categorize corretamente as receitas externas.",
                  "Revise os relatórios e o fluxo de caixa no encerramento do período."
                ].map((item, index) => (
                  <div
                    key={item}
                    className="flex items-start gap-3 bg-slate-50 border border-slate-200 rounded-xl p-4"
                  >
                    <CheckCircle2 size={20} className="text-emerald-600 shrink-0 mt-0.5" />
                    <span className="text-sm text-gray-700">
                      <strong>{index + 1}.</strong> {item}
                    </span>
                  </div>
                ))}
              </div>
            </div>

            <div className="bg-white p-8 rounded-3xl border border-purple-200 shadow-sm relative overflow-hidden">
              <div className="absolute top-0 left-0 w-2 h-full bg-purple-500" />
              <h3 className="text-xl font-bold text-gray-900 mb-3 flex items-center gap-3">
                <span className="w-8 h-8 rounded-full bg-purple-100 text-purple-700 flex items-center justify-center text-sm font-black">11</span>
                Resumo financeiro contextual no prontuário
              </h3>
              <p>Na aba <strong>Financeiro</strong> do prontuário, use <strong>Gerar resumo</strong> para organizar valores em aberto, vencimentos, próximos 30 dias, pagamentos parciais e carteira do paciente.</p>
              <div className="mt-4 bg-purple-50 p-4 rounded-xl border border-purple-200 flex items-start gap-3">
                <Sparkles className="text-purple-600 shrink-0 mt-1" size={20} />
                <p className="text-purple-900 text-sm mb-0">O resultado é uma fotografia dos lançamentos atuais. Não prevê inadimplência, não recomenda cobranças e não altera valores, vencimentos ou status.</p>
              </div>
            </div>
          </div>

          <div className="bg-green-50 border border-green-200 rounded-3xl p-8 mt-12">
            <h3 className="text-2xl font-bold text-green-900 mt-0 mb-4 flex items-center gap-3">
              <ShieldCheck size={26} />
              Boas práticas
            </h3>
            <ul className="space-y-3 mb-0">
              <li>não registre a mesma receita em mais de uma fatura;</li>
              <li>não marque uma cobrança como recebida antes da confirmação do pagamento;</li>
              <li>informe corretamente vencimentos, competência e datas de pagamento;</li>
              <li>use categorias consistentes para despesas e receitas externas;</li>
              <li>vincule a NFS-e à fatura correspondente sempre que possível;</li>
              <li>confira o período antes de interpretar um relatório;</li>
              <li>revise cobranças vencidas regularmente;</li>
              <li>faça a conciliação dos dados do sistema com extratos e documentos da clínica.</li>
            </ul>
          </div>

          <div className="bg-indigo-900 rounded-3xl p-8 mt-12 text-white">
            <h3 className="text-2xl font-bold mb-4 flex items-center gap-3">
              <Landmark size={26} />
              Recursos do Financeiro
            </h3>
            <ul className="space-y-3">
              {[
                "Faturas de pacientes e receitas externas.",
                "Cobranças únicas ou parceladas.",
                "Créditos antecipados do paciente.",
                "Registro e consulta de recebimentos.",
                "Gestão de despesas, categorias e fornecedores.",
                "Importação, visualização e vínculo de NFS-e.",
                "Cadastro de parceiros financeiros.",
                "Relatórios gerenciais e fluxo de caixa.",
                "Integração com o prontuário e o Dashboard Executivo."
              ].map((item) => (
                <li key={item} className="flex items-start gap-3">
                  <CheckCircle2 className="text-indigo-400 shrink-0 mt-1" size={20} />
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </div>

          <div className="bg-white border border-gray-100 rounded-3xl p-8 mt-8 shadow-sm">
            <h3 className="text-xl font-bold text-gray-900 mt-0 mb-3 flex items-center gap-2">
              <Lightbulb size={22} className="text-indigo-600" />
              Próximo passo recomendado
            </h3>
            <p>
              Depois de manter os lançamentos financeiros atualizados, utilize o
              Dashboard Executivo para acompanhar riscos, metas, tendências,
              inadimplência, margem e oportunidades de gestão.
            </p>
            <Link
              to="/ajuda/tutoriais/dashboard"
              className="not-prose inline-flex items-center gap-2 bg-indigo-600 hover:bg-indigo-700 text-white px-5 py-3 rounded-xl font-bold transition-colors"
            >
              Conhecer o Dashboard Executivo
              <ArrowRight size={18} />
            </Link>
          </div>
        </article>
      </main>
    </div>
  );
}
